<template>
  <div class="card bg-white flex shadow-light">
    <div class="header bg-secondary px-6 flex col items-center justify-center">
      <div class="price flex col items-center justify-center gap-5">
        <h3 class="flex col items-center gap-1 mb-0 text-white">
          <CountryFlag :code="getVisaCountryIsoCode(product.country)" size="large" />
          {{ $tt(product.country) }}
        </h3>
        <h4 class="text-white">
          <FromPrice :price="product.price" />
        </h4>
      </div>
      <ProductFeatureList :product :bullets />
      <DesktopOnly>
        <AppBtn :to="ctaPath" size="large" primary still>
          {{ $tt(`page.get_visa_cta_${product.country}`) }}
        </AppBtn>
      </DesktopOnly>
    </div>
    <div class="content pa-7">
      <p class="lead mb-6">
        {{ $tt(`home_page.overview_lead_${product.country}`) }}
      </p>
      <div class="images flex gap-7">
        <CountriesOverviewCarouselCardImage
          :photo="images.teaser"
          :text="$tt(`home_page.overview_body2_${product.country}`)"
        />
        <div class="desc flex col justify-between">
          <DesktopOnly tag="p" class="body-text">
            {{ $tt(`home_page.overview_body1_${product.country}`) }}
          </DesktopOnly>
          <div class="message bg-tertiary flex py-4 px-5">
            <p>
              {{ $tt(`home_page.overview_body2_${product.country}`) }}
            </p>
            <AppImg :src="images.icon.src" :alt="$t(images.icon.alt!)" />
          </div>
        </div>
      </div>
      <MobileOnly>
        <AppBtn :to="ctaPath" size="large" primary block still>
          {{ $tt(`page.get_visa_cta_${product.country}`) }}
        </AppBtn>
      </MobileOnly>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ProductFeatures } from '~/types';
import { getVisaCountryIsoCode, getVisaCountryPath } from '~/helpers/visa-country';
import { getCountryImages } from '~/helpers/image-maps';

const props = defineProps<ProductFeatures>();

const images = getCountryImages(props.product.country);
const ctaPath = getVisaCountryPath(props.product.country);
</script>

<style lang="scss" scoped>
.card {
  border-radius: var(--border-r-md);
  max-width: calc(100vw - 2 * var(--space-7));
  width: 1160px;
  line-height: var(--lh-md);

  .header {
    border-start-start-radius: var(--border-r-md);
    border-end-start-radius: var(--border-r-md);
    min-width: 360px;
    h4 {
      text-decoration: underline;
    }
    button {
      box-shadow: 0 0 20px 0 rgba(0, 0, 0, 0.35) !important;
    }
  }

  .content {
    overflow: auto;
    .lead {
      font-weight: 500;
    }
    .desc {
      max-width: 320px;
    }
    .message {
      position: relative;
      border-radius: var(--border-r-lg);
      p {
        font-weight: 500;
        color: #1e1e1e;
      }
      img {
        align-self: flex-start;
        margin-inline-end: calc(-1*(var(--space-1)));
      }
    }
    .message::after {
      content: "";
      position: absolute;
      bottom: -28px;
      inset-inline-start: 35px;
      width: 0;
      height: 0;
      border-width: 15px;
      border-style: solid;
      border-color: rgb(var(--v-theme-tertiary)) transparent transparent transparent;
    }
  }
}

@media (min-width: 851px) and (max-width: 1200px) {
  .header {
    min-width: 280px !important;
    width: auto !important;
  }
}

@media (max-width: 850px) {
  .card {
    flex-direction: column;
    width: 100%;
    max-width: calc(100vw - (2 * var(--space-4)));
    height: 100%;
    .header, .content {
      width: 100%;
      min-width: auto;
    }

    .header {
      border-radius: var(--border-r-md);
      padding: var(--space-4) !important;
      .price {
        width: 100%;
        flex-direction: row;
        justify-content: space-between;
        margin-bottom: var(--space-4);
      }
      h3 {
        flex-direction: row;
        gap: var(--space-2);
        font-size: 18px;
      }
      h4 {
        font-size: 18px;
        margin-bottom: 0;
      }
    }

    .content {
      padding: var(--space-4) var(--space-4) var(--space-5) !important;
      .body-text {
        font-size: 12px;
        margin-bottom: 0 !important;
      }
      .lead {
        font-size: 14px;
        margin-bottom: var(--space-5) !important;
      }
      .images {
        flex-direction: column;
        gap: var(--space-5);
        margin-top: var(--space-4);
        margin-bottom: var(--space-7);
      }
      .desc {
        max-width: none;
      }
      .message {
        display: flex;
        justify-content: space-between;
        p {
          font-size: 14px;
        }
      }
    }
  }
}
</style>